import React, { useMemo } from 'react'
import styled from 'styled-components'
import { useFormikContext } from 'formik'

import { IconButton, Text, TextButton, TextInput } from 'components'

// Utils and Services
import useWorkFlowLookup from 'hooks/lookup/use-workflow-lookup'
import useFormikError from 'hooks/use-formik-error'

// Types
import { WidthProps } from 'types/styled'
import { LeadDetails } from 'types/global'
import { DUP_CHECK_ERRORS } from 'modules/Leads/NewLead/constants'
import DuplicateErrorMsg from 'modules/Leads/shared/DuplicateErrorMsg'

type Props = {
  width?: WidthProps
  displayName?: string
  disabled?: boolean
  name?: string
  isLoading?: boolean
}

const EmailAddressGroup: React.FC<Props> = (props) => {
  const { disabled, name, width = 'auto' } = props

  const { contactInfoTypes } = useWorkFlowLookup({})

  const { values, setFieldTouched, setFieldValue } = useFormikContext<LeadDetails.LeadDetailsForm>()
  const [getErrMsg] = useFormikError<LeadDetails.LeadDetailsForm>()

  const contactInfoType = useMemo(
    () => contactInfoTypes.find((item) => item.name.toLowerCase() === 'email')?.id || '',
    [contactInfoTypes],
  )
  const emails = useMemo(
    () => values.contactPerson.email || [{ contactInfoTypeId: contactInfoType, isPrimary: true, value: '' }],
    [contactInfoType, values.contactPerson.email],
  )

  const handleChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(`${name}[${index}].value`, e.target.value)
  }

  const handleChangePrimary = (index: number) => {
    if (emails[index].isPrimary) return

    const newValues = emails.map((item, idx) => ({
      ...item,
      isPrimary: index === idx,
    }))

    setFieldValue(`${name}`, newValues)
  }

  const handleOnDelete = (idx: number) => {
    let newValues = [...emails]
    const isPrimaryEntry = newValues[idx].isPrimary

    newValues = newValues.filter((_, index) => index !== idx)
    if (isPrimaryEntry) {
      newValues[0].isPrimary = true
    }

    setFieldValue(`${name}`, newValues)
  }

  return (
    <Container width={width}>
      <Text color={disabled ? 'gray4' : 'defaultText'} fontSize={14} lineHeight="24px" mb="4px">
        email_address_lbl
      </Text>
      {emails.map((emailData, index) => {
        const fieldName = `${name}[${index}].value`
        const errMsg = getErrMsg(fieldName as keyof LeadDetails.LeadDetailsForm)
        const isPotentialDupErr = errMsg === DUP_CHECK_ERRORS.POTENTIAL_DUP_EMAIL
        const isDuplicateError = errMsg === DUP_CHECK_ERRORS.EXACT_DUP_EMAIL || isPotentialDupErr

        return (
          <ItemContainer key={index}>
            <EmailContainer>
              <TextInput
                disabled={disabled}
                onChange={handleChange(index)}
                placeholder="please_enter_lbl"
                value={emailData.value}
                error={errMsg}
                errorVariant={isPotentialDupErr ? 'warning' : 'error'}
                required
                onBlur={() => {
                  setFieldTouched(`${name}[${index}].value`, true)
                }}
                customError={
                  isDuplicateError ? (
                    <DuplicateErrorMsg message={errMsg} type={isPotentialDupErr ? 'warning' : 'error'} />
                  ) : undefined
                }
              />
            </EmailContainer>
            {!disabled && (
              <ActionsContainer>
                <MarkPrimaryIcon
                  icon={emailData.isPrimary ? 'flag' : ['far', 'flag']}
                  isPrimary={emailData.isPrimary}
                  onClick={() => handleChangePrimary(index)}
                  tooltip="mark_as_primary_lbl"
                  tooltipPosition="bottom"
                  variant="ghost"
                />
                {emails.length > 1 && (
                  <DeleteIcon icon="trash-alt" onClick={() => handleOnDelete(index)} variant="blandGhost" />
                )}
              </ActionsContainer>
            )}
          </ItemContainer>
        )
      })}
      {!disabled && (
        <StyledTextButton
          icon="plus"
          inline
          onClick={() =>
            setFieldValue(`${name}`, [
              ...emails,
              { value: '', contactInfoTypeId: contactInfoType, isPrimary: false, error: '', id: '' },
            ])
          }
        >
          add_new_email_btn
        </StyledTextButton>
      )}
    </Container>
  )
}

EmailAddressGroup.displayName = 'EmailAddressGroup'
export default EmailAddressGroup

const Container = styled.div<{ width?: WidthProps }>`
  display: block;
  width: ${(p) => (p.width === 'auto' ? '100%' : `${p.width}px`)};
`

const ItemContainer = styled.div`
  align-items: start;
  display: flex;
  margin-bottom: 8px;
`

const ActionsContainer = styled.div`
  display: inline-flex;
  margin: 6px;
  align-items: center;
`

const EmailContainer = styled.div<{ width?: WidthProps }>`
  display: inline-block;
  width: 100%;
`

const StyledTextButton = styled((p) => <TextButton {...p} />)`
  margin: 4px 0;
`

const DeleteIcon = styled(IconButton)`
  svg {
    color: ${({ theme }) => theme.colors.gray4};
  }
`

const MarkPrimaryIcon = styled(IconButton)<{ isPrimary?: boolean }>`
  svg {
    color: ${({ isPrimary, theme }) => (isPrimary ? theme.colors.orange : theme.colors.gray4)};
  }

  &:hover {
    svg {
      color: ${(p) => p.theme.colors.orange};
    }
  }
`
